<template>
  <v-dialog
    :model-value="show"
    @update:model-value="
      {
        if (!state.loading) $emit('update:show', $event);
      }
    "
  >
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <div>Breaks for {{ dayjs(day.start).format("MMM D") }}</div>
        <v-btn
          @click="
            {
              if (!state.loading) $emit('update:show', false);
            }
          "
          icon
          color="transparent"
          data-cy="break-modal-close"
          ><v-icon>mdi-close</v-icon></v-btn
        >
      </v-card-title>
      <v-card-item>
        <div class="m-3 d-flex gap-3">
          <!-- Left Column with DatePickers -->
          <div class="d-flex flex-column gap-3 align-left">
            <div v-for="breakItem in breaks" :key="breakItem.break_id">
              <v-icon color="gray500" @click="editBreak(breakItem)">mdi-note-edit</v-icon>
              {{ formatTime(breakItem.start_time) }} - {{ formatTime(breakItem.end_time) }}
              <v-icon color="gray500" @click="confirmDelete(breakItem)"
                >mdi-trash-can-outline</v-icon
              >
            </div>
            <div><v-btn @click="newBreak" data-cy="add-new-break-btn">Add New Break</v-btn></div>
          </div>

          <!-- Vertical Line -->
          <div style="border-left: 1px solid darkgrey"></div>

          <!-- <div class="flex-grow-12 mx-auto d-flex flex-wrap gap-3 align-center" v-if="BreakClicked && currentBreakItem?.start_time !== undefined">
                    Confirm that you want to delete the {{ dayjs(currentBreakItem.start_time, { format: 'HH:mm:ss' }).format('h:mm A') }} - {{ dayjs(currentBreakItem.end_time, { format: 'HH:mm:ss' }).format('h:mm A') }} break.
                <v-btn>Delete</v-btn>
                <v-btn variant="outlined" @click="BreakClicked=false;">Cancel</v-btn>
                </div> -->
          <div
            class="flex-grow-12 mx-auto d-flex flex-column gap-1 align-center"
            v-if="!BreakClicked && !AddBreakClicked"
          >
            <div>
              You can create breaks for all teachers/teams for
              {{ dayjs(day.start).format("MMM D") }}
            </div>
            <img src="/break-time.jpg" style="height: 25vh" />
          </div>
          <div
            class="flex-grow-12 mx-auto d-flex flex-wrap gap-3 align-center"
            v-if="BreakClicked && currentBreakItem?.start_time && currentBreakItem?.end_time"
          >
            Confirm that you want to delete the {{ formatTime(currentBreakItem.start_time) }} -
            {{ formatTime(currentBreakItem.end_time) }} break.
            <v-btn @click="deleteBreak(currentBreakItem.break_id)">Delete</v-btn>
            <v-btn variant="outlined" @click="BreakClicked = false">Cancel</v-btn>
          </div>

          <!-- Right Column with Hello Text -->

          <div
            class="flex-grow-12 mx-auto d-flex flex-column gap-3 align-center"
            v-if="AddBreakClicked"
          >
            Set the time range for your new break.
            <div class="flex-grow-12 mx-auto d-flex flex-wrap gap-3 align-center">
              <DatePicker
                style="width: 150px; height: 30px"
                label="Start"
                v-model="state.start"
                type="time"
                :min="dayjs(day.start).subtract(1, 'minutes').toDate()"
                :max="state.end"
                color="primary"
                data-cy="break-bulk-start"
              ></DatePicker>
              <DatePicker
                style="width: 150px; height: 30px"
                label="End"
                v-model="state.end"
                type="time"
                :min="dayjs(state.start).add(1, 'minutes').toDate()"
                :max="
                  dayjs(day.end)
                    .add(day.time_interval + 1, 'minutes')
                    .toDate()
                "
                color="primary"
                data-cy="break-bulk-end"
              ></DatePicker>
            </div>
            <div class="flex-grow-12 mx-auto flex-column gap-1">
              <v-switch
                color="primary"
                class="custom-input checkbox"
                label="Apply breaks to all teachers"
                data-cy="event-option-breaks-all-teachers"
                density="compact"
                v-model="state.has_teachers"
              ></v-switch>
              <v-switch
                color="primary"
                class="custom-input checkbox"
                label="Apply breaks to all teams"
                data-cy="event-option-breaks-all-teams"
                density="compact"
                v-model="state.has_teams"
              ></v-switch>
            </div>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                variant="flat"
                v-if="currentBreakItem?.break_id"
                @click="update(currentBreakItem)"
                data-cy="break-update"
                :loading="state.loading"
                >Update</v-btn
              >
              <v-btn
                v-else
                variant="flat"
                @click="save"
                :loading="state.loading"
                data-cy="break-bulk-save"
                >Save</v-btn
              >
              <v-btn variant="outlined" @click="AddBreakClicked = false">Cancel</v-btn>
            </v-card-actions>
          </div>
        </div>
      </v-card-item>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import type { ConferenceDay } from "@/api_types/teacher";
import type { BreakItem } from "@/api_types/conference";
import DatePicker from "@/components/datePicker.vue";
import FetchService from "@/services/fetch.service";
import { useAlertsStore } from "@/stores/alerts";
import dayjs from "dayjs";
import { watch } from "vue";
import { ref, reactive } from "vue";

const AddBreakClicked = ref(false);
const BreakClicked = ref(false);
const currentBreakItem = ref<BreakItem>();
const breaks = ref<BreakItem[]>([]);
const props = defineProps<{ show: boolean; day: ConferenceDay; tab: string }>();
defineEmits<{
  (event: "update:show", val: boolean): void;
}>();
const state = reactive({
  start: dayjs().set("hours", 8).set("minutes", 0).toDate(),
  end: dayjs().set("hours", 17).set("minutes", 0).toDate(),
  has_teachers: false,
  has_teams: false,
  loading: false
});

watch([() => props.day], () => {
  Object.assign(state, {
    start: dayjs(props.day.start).toDate(),
    end: dayjs(props.day.end).toDate(),
    has_teachers: false,
    has_teams: false
  });
  getBreaks();
});

function newBreak() {
  BreakClicked.value = false;
  AddBreakClicked.value = true;
  currentBreakItem.value = undefined;
  state.start = dayjs(props.day.start).toDate();
  state.end = dayjs(props.day.end).toDate();
  state.has_teachers = false;
  state.has_teams = false;
}

function editBreak(item: BreakItem) {
  console.log(item);
  BreakClicked.value = false;
  AddBreakClicked.value = true;
  currentBreakItem.value = item;
  state.has_teachers = item.has_teachers;
  state.has_teams = item.has_teams;
  console.log(state.has_teachers);
  state.start = dayjs(item.start_time).toDate();
  state.end = dayjs(item.end_time).toDate();
}

function confirmDelete(item: BreakItem) {
  BreakClicked.value = true;
  AddBreakClicked.value = false;
  currentBreakItem.value = item;
}

function formatTime(breakTime) {
  if (breakTime) return dayjs(breakTime, { format: "YYYY-MM-DD HH:mm:ss" }).format("h:mm A");
  else return "";
}

const deleteBreak = async (id: Number) => {
  state.loading = true;
  const res = await FetchService.fetchData(`/conference-breaks/${id}`, "delete");
  if (res?.success) {
    useAlertsStore().addAlert("success", "Break deleted successfully");
  } else {
    useAlertsStore().addAlert("error", "Failed to delete break, please try again later");
  }
  state.loading = false;
  BreakClicked.value = false;
  AddBreakClicked.value = false;
  currentBreakItem.value = undefined;
  getBreaks();
};

const getBreaks = async () => {
  state.loading = true;
  if (props.day && props?.day?.conference_days_id !== undefined) {
    const breakRes: BreakItem[] = await FetchService.fetchData(
      `/conference-breaks/${props.day.conference_days_id}`,
      "get"
    );
    breaks.value = breakRes;
  }
  state.loading = false;
};
getBreaks();

const update = async (item: BreakItem) => {
  state.loading = true;
  const start_date = dayjs(props.day.start).format("YYYY-MM-DD");
  const start_time = dayjs(state.start).format("HH:mm:00");
  const end_time = dayjs(state.end).format("HH:mm:00");
  const breakRes = await FetchService.fetchData(`/conference-breaks/${item.break_id}`, "put", {
    start_time: dayjs(start_date + " " + start_time).format("YYYY-MM-DD HH:mm:ss"),
    end_time: dayjs(start_date + " " + end_time).format("YYYY-MM-DD HH:mm:ss"),
    conference_days_id: props.day.conference_days_id,
    has_teachers: state.has_teachers,
    has_teams: state.has_teams
  });

  if (breakRes?.success) {
    useAlertsStore().addAlert("success", breakRes?.message);
  } else {
    useAlertsStore().addAlert("error", breakRes?.message);
  }
  state.loading = false;
  getBreaks();
};

const save = async () => {
  state.loading = true;
  const start_date = dayjs(props.day.start).format("YYYY-MM-DD");
  const start_time = dayjs(state.start).format("HH:mm:00");
  const end_time = dayjs(state.end).format("HH:mm:00");
  const breakRes = await FetchService.fetchData("/conference-breaks", "post", {
    start_time: dayjs(start_date + " " + start_time).format("YYYY-MM-DD HH:mm:ss"),
    end_time: dayjs(start_date + " " + end_time).format("YYYY-MM-DD HH:mm:ss"),
    conference_days_id: props.day.conference_days_id,
    has_teachers: state.has_teachers,
    has_teams: state.has_teams
  });

  if (breakRes?.success) {
    useAlertsStore().addAlert("success", "Break added successfully");
  } else {
    useAlertsStore().addAlert("error", "Failed to add break, please try again later");
  }
  state.loading = false;
  getBreaks();
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;
</style>
